import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// 0.55.2 — Inbox-Service. Read-only über die approval_requests-
// Projection, die Events aus vier Quellen (developer_registrations,
// api_subscriptions, application_promotions, platform_approvals)
// konsolidiert. Siehe ADR-008.
//
// Nicht zu verwechseln mit ApprovalsService (0.45.1), der die Two-
// Person-Rule auf /v1/platform/approvals bedient. Der Inbox-Service
// spricht ausschliesslich /v1/approvals und macht nie approve/reject —
// Actions bleiben auf den jeweiligen Source-Endpunkten.

export interface ApprovalRow {
  id: string;
  source:
    | 'dev_registrations'
    | 'api_subscriptions'
    | 'application_promotions'
    | 'platform_approvals'
    | 'app_delete_requests';
  source_id: string;
  kind: string;
  target_ref: string;
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'expired';
  initiator_identity_id?: string;
  assignee_persona?: string;
  approver_identity_id?: string;
  title: string;
  summary: string;
  /**
   * Structured parameters beside the sentence (cut U2, migration 192).
   * `inbox.component.ts` renders `i18n.fap.module.inbox.kind_<kind>_*` from
   * this bag and falls back to `title` and `summary` per key. Absent on rows
   * written before 192, and PRESENT BUT INCOMPLETE on two measured shapes:
   * a decision that reached the projector before its request carries
   * `{decision: ...}` alone, and the sandbox request path stores
   * `application` as a UUID. A reader must therefore check the keys it
   * places rather than the bag.
   */
  params?: Record<string, string> | null;
  created_at: string;
  decided_at?: string;
}

export interface InboxListResponse {
  items: ApprovalRow[];
  total: number;
}

export interface InboxQuery {
  status?: string; // comma-sep, e.g. 'pending,approved'
  source?: string; // comma-sep
  kind?: string;   // comma-sep
  assignee?: 'me'; // today only 'me' supported
  limit?: number;
  offset?: number;
}

@Injectable({ providedIn: 'root' })
export class InboxService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/v1/approvals`;

  list(q: InboxQuery = {}): Observable<InboxListResponse> {
    let p = new HttpParams();
    if (q.status)   p = p.set('status', q.status);
    if (q.source)   p = p.set('source', q.source);
    if (q.kind)     p = p.set('kind', q.kind);
    if (q.assignee) p = p.set('assignee', q.assignee);
    if (q.limit)    p = p.set('limit', String(q.limit));
    if (q.offset)   p = p.set('offset', String(q.offset));
    return this.http.get<InboxListResponse>(this.base, { params: p });
  }

  get(id: string): Observable<ApprovalRow> {
    return this.http.get<ApprovalRow>(`${this.base}/${encodeURIComponent(id)}`);
  }
}
